import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import * as filterGroupSelectors from 'data/filterGroup/filterGroup.selectors';
import { RootState } from 'data/root.reducers';
import * as filterGroupActions from 'data/filterGroup/filterGroup.actions';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FilterGroup } from 'data/filterGroup/filterGroup.types';
import FilterGroupForm from 'routes/FilterGroup/components/FilterGroupForm';
import * as types from 'data/filterGroup/filterGroup.types';
import { filterGroupTemplate } from '../../templates';
import { adaptTemplateVisibilityMode } from '../../utils/filterGroupTemplate.adapter';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.FilterGroup
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  selectedFilterGroup: filterGroupSelectors
    .getSelectedFilterGroup(state)[0],
  filterGroup: filterGroupSelectors.getFilterGroup(state),
});

const mapDispatch = {
  goToFilterGroupList: filterGroupActions.goToFilterGroupList,
  goToFilterGroupEdit: filterGroupActions.goToFilterGroupEdit,
  getFilterGroup: filterGroupActions.getFilterGroup,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const FilterGroupView = ({
  history,
  selectedFilterGroup,
  filterGroup,
  goToFilterGroupList,
  goToFilterGroupEdit,
  getFilterGroup,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<FilterGroup>>({});
  const [template, setTemplate] = useState(adaptTemplateVisibilityMode(filterGroupTemplate, 'view'));
  const { t } = useTranslation();

  const id = selectedFilterGroup?.id;

  useEffect(() => {
    if (id) {
      getFilterGroup(id);
    }
  }, [id, getFilterGroup]);

  useEffect(() => {
    if (filterGroup != null && template != null) {
      const clonedFilterGroup = cloneDeep(filterGroup);
      const clonedTemplate = cloneDeep(template);
      clonedTemplate.formId = Date.now().toString();

      setTemplate(clonedTemplate);
      setFormData(clonedFilterGroup);
    }
  }, [filterGroup, id]);

  const handleClickEdit = () => goToFilterGroupEdit(history);

  const handleCancel = () => goToFilterGroupList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as FilterGroup);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <FilterGroupForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleClickEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (filterGroup == null) {
    return <Fragment />;
  }

  return (
    <FormLayout>
      <FormLayoutContent>
        {renderFormPanel(t('filterGroup.view.title'))}
        <div className="read-only">
          {renderForm()}
        </div>
      </FormLayoutContent>
    </FormLayout>
  );
};

export default withRouter(connector(FilterGroupView));
